import React from 'react';
import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import { sendTTUEvents } from '@events/sendAnalytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import { getIsRepeatUser } from '@stores/selectors/ttuSelectors';
import useTTUStore from '@stores/ttu';
import { mediaQuery } from '@styles/index';
import { UAITS_EVENT_MAP } from './constants';
import { useTestSeriesData } from './useTestSeriesData';

const HelpSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 100px 0;
  scroll-margin-top: 100px;
  gap: 12px;
  align-items: center;
  justify-content: center;

  ${mediaQuery.uptoMobile} {
    padding: 40px 0;
  }
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 64px;
  line-height: 120%;
  letter-spacing: -2%;
  text-align: center;
  color: var(--color-primary-focus);

  ${mediaQuery.uptoMobile} {
    font-size: 40px;
  }
`;

const SubTitle = styled.p`
  font-weight: 400;
  font-size: 32px;
  line-height: 150%;
  letter-spacing: -1px;
  text-align: center;
  color: var(--color-text-secondary);

  ${mediaQuery.uptoMobile} {
    font-size: 18px;
  }
`;

const SupportImage = styled.img`
  max-width: 300px;
  object-fit: contain;
  margin: 40px 0;
`;

const SupportCTA = styled.div`
  width: 328px;
  height: 48px;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0%;
  color: var(--color-text-primary);
`;

const SupportCTAImage = styled.img``;

const SupportCTAText = styled.p`
  font-weight: 600;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 2%;
  text-align: center;
  vertical-align: middle;
  color: var(--color-text-primary);
`;

const SupportCTABoldText = styled.span`
  font-weight: 700;
  letter-spacing: 2%;
  text-align: center;
  text-transform: uppercase;
  color: var(--color-text-primary);
`;

const SupportCallSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HelpSection = ({ setShowTTU }) => {
  const meData = useMyInfo(getMe);
  const { selectedGoal } = useSelectedGoal();
  const isRepeatUser = useTTUStore(getIsRepeatUser);
  const { data } = useTestSeriesData();
  if (!data?.helpSection) return null;
  const { title, description, ctaText, contact, image } = data.helpSection;

  const handleTTUClick = () => {
    setShowTTU(true);
    sendTTUEvents(
      UAITS_EVENT_MAP.TTU_CLICKED,
      {
        user_ttu_status: isRepeatUser ? 'Repeat' : 'New',
        source: 'UAITS page',
        last_primary_source_section: 'UAITS page'
      },
      meData?.uid,
      meData?.username,
      selectedGoal?.uid,
      selectedGoal?.name
    );
  };
  return (
    <HelpSectionWrapper id="help">
      {title && <Title>{title}</Title>}
      {description && <SubTitle>{description}</SubTitle>}
      {image?.url && <SupportImage src={image.url} />}
      {ctaText && <SupportCTA onClick={handleTTUClick}>{ctaText}</SupportCTA>}
      <SupportCallSection>
        <SupportCTAImage src={getImage('testseries/call.svg')} />
        <SupportCTAText>
          Or call us on{' '}
          {contact && <SupportCTABoldText>{contact}</SupportCTABoldText>}
        </SupportCTAText>
      </SupportCallSection>
    </HelpSectionWrapper>
  );
};

export default HelpSection;
